import { useCallback, useRef, useState } from 'react';
import { dispatchSmartLinkExternalAuthEvent } from '@post-office/rovo-expansion-common';

import { FULL_CONNECTOR_SOURCE } from '@atlassian/rovo-connectors-config/schemas/types';
import {
	type SearchCombinedConfigPayload,
	updateBaseCombinedConfigCacheEntry,
	updateCombinedConfigCacheEntry,
} from '@atlassian/search-client/config';

import type {
	ConnectorOAuthErrorSubtype,
	ConnectorOAuthLifecycleEvent,
	ConnectorOAuthStatus,
} from '@confluence/connectors/entry-points/connector-oauth-types.ts';
import { useConnectorOAuth } from '@confluence/connectors/entry-points/use-connector-oauth.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';

import { classifyConnectorRow } from './connectors/classifyConnectorRow.ts';
import { resolveAppIdForConnectorName } from './connectors/connectorTypeNames.ts';
import type { ConnectorRow } from './thirdPartyPromptTypes.ts';

export type ThirdPartyConnectFailureReason =
	| ConnectorOAuthErrorSubtype
	/** No auth URL, no session, or an app the connector map does not know. */
	| 'unavailable';

export type ThirdPartyConnectContext = {
	appId: string;
	connectorType: string | null;
};

/** The consumer owns the event names and attributes; this hook only decides when each fires. */
export type ThirdPartyConnectAnalytics = {
	onConnectStarted?: (context: ThirdPartyConnectContext) => void;
	onConnectSucceeded?: (context: ThirdPartyConnectContext & { durationMs: number }) => void;
	onConnectCancelled?: (context: ThirdPartyConnectContext) => void;
	onConnectFailed?: (
		context: ThirdPartyConnectContext & { reason: ThirdPartyConnectFailureReason },
	) => void;
};

export type UseThirdPartyConnectorOAuthOptions = {
	/** `AppId` of the product the prompt offers, e.g. `drive`. */
	appId: string;
	/** Raw connector rows. Never the merged `thirdPartyConfigs`. */
	connectorRows: ConnectorRow[];
	cloudId?: string;
	userId?: string | null;
	analytics?: ThirdPartyConnectAnalytics;
};

export type UseThirdPartyConnectorOAuthResult = {
	status: ConnectorOAuthStatus;
	failureReason: ThirdPartyConnectFailureReason | null;
	/** True once a connect succeeded and the config cache write settled. */
	isPromptHidden: boolean;
	connect: () => Promise<void>;
};

type ResolvedConnector = {
	authUrl: string | undefined;
	/** The row's `name`. The config cache calls the same value `type`. */
	connectorType: string | null;
	userNeedsOAuth: boolean | undefined;
};

const NOTHING_RESOLVED: ResolvedConnector = {
	authUrl: undefined,
	connectorType: null,
	userNeedsOAuth: undefined,
};

const markConnectedInCombinedConfig =
	(connectorName: string) =>
	(config: SearchCombinedConfigPayload): SearchCombinedConfigPayload => ({
		...config,
		thirdPartyProducts: config.thirdPartyProducts?.map((product) =>
			product.name === connectorName ? { ...product, isUserOAuthed: true } : product,
		),
	});

const resolveConnector = (connectorRows: ConnectorRow[], appId: string): ResolvedConnector => {
	// Only the full connector carries the address the button needs. The Smart Links
	// row for the same product proves the grant and has its own unrelated address.
	const fullConnectorRow = connectorRows.find(
		(row) =>
			Boolean(row.outboundAuthUrl) &&
			classifyConnectorRow(row) === FULL_CONNECTOR_SOURCE &&
			resolveAppIdForConnectorName(row.name) === appId,
	);

	if (!fullConnectorRow) {
		return NOTHING_RESOLVED;
	}

	return {
		authUrl: fullConnectorRow.outboundAuthUrl ?? undefined,
		connectorType: fullConnectorRow.name,
		userNeedsOAuth: !fullConnectorRow.isUserOAuthed,
	};
};

export function useThirdPartyConnectorOAuth({
	appId,
	connectorRows,
	cloudId,
	userId,
	analytics,
}: UseThirdPartyConnectorOAuthOptions): UseThirdPartyConnectorOAuthResult {
	const { authUrl, connectorType, userNeedsOAuth } = resolveConnector(connectorRows, appId);

	const [failureReason, setFailureReason] = useState<ThirdPartyConnectFailureReason | null>(null);
	const [isPromptHidden, setIsPromptHidden] = useState(false);

	const analyticsRef = useRef(analytics);
	analyticsRef.current = analytics;

	const reportFailure = useCallback(
		(reason: ThirdPartyConnectFailureReason) => {
			analyticsRef.current?.onConnectFailed?.({ appId, connectorType, reason });

			// analytics is optional, so monitoring is the only signal a consumer cannot drop.
			getMonitoringClient().submitError(
				new Error(`Third-party connect failed for ${appId}: ${reason}`),
				{ attribution: Attribution.INTEGRATIONS },
			);

			setFailureReason(reason);
		},
		[appId, connectorType],
	);

	const completeSuccess = useCallback(() => {
		dispatchSmartLinkExternalAuthEvent(appId);

		if (connectorType && cloudId && userId) {
			const markConnected = markConnectedInCombinedConfig(connectorType);

			// Detection re-reads this config, so the prompt returns on the next page without this.
			// Two cache entries back it and the reader picks one at runtime, so patch both.
			void Promise.all([
				updateCombinedConfigCacheEntry(userId, cloudId, markConnected),
				updateBaseCombinedConfigCacheEntry(cloudId, markConnected),
			]).then((didUpdate) => {
				if (!didUpdate.some(Boolean)) {
					getMonitoringClient().submitError(
						new Error(`Third-party connect did not update the combined config for ${appId}`),
						{ attribution: Attribution.INTEGRATIONS },
					);
				}
			});
		}

		setFailureReason(null);
		setIsPromptHidden(true);
	}, [appId, connectorType, cloudId, userId]);

	const onLifecycleEvent = useCallback(
		(event: ConnectorOAuthLifecycleEvent) => {
			const context: ThirdPartyConnectContext = { appId, connectorType };

			switch (event.type) {
				case 'auth-started':
					analyticsRef.current?.onConnectStarted?.(context);
					break;
				case 'auth-succeeded':
					// Before the analytics call, so a consumer that throws cannot stop the dispatch.
					completeSuccess();
					analyticsRef.current?.onConnectSucceeded?.({ ...context, durationMs: event.durationMs });
					break;
				case 'auth-cancelled':
					analyticsRef.current?.onConnectCancelled?.(context);
					break;
				case 'auth-failed':
					// Carries the popup-blocked subtype too, so auth-aborted needs no handling.
					reportFailure(event.errorSubtype);
					break;
				default:
					break;
			}
		},
		[appId, completeSuccess, connectorType, reportFailure],
	);

	const { status, connect: startConnectorOAuth } = useConnectorOAuth({
		connector: {
			productKey: appId,
			resourceType: connectorType ?? undefined,
			outboundAuthUrl: authUrl,
			userNeedsOAuth,
		},
		cloudId,
		userId,
		onLifecycleEvent,
	});

	const connect = useCallback(async () => {
		setFailureReason(null);

		if (!authUrl || !cloudId || !userId) {
			reportFailure('unavailable');
			return;
		}

		try {
			// Awaited so the caller can show a spinner. onLifecycleEvent handles the outcome.
			await startConnectorOAuth();
		} catch {
			// onLifecycleEvent already recorded the precise reason.
		}
	}, [authUrl, cloudId, reportFailure, startConnectorOAuth, userId]);

	return {
		status,
		failureReason,
		isPromptHidden,
		connect,
	};
}
